import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Colors, Typography } from '../../theme';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import { useKeyboardInsets } from '../../hooks/useKeyboardInsets';
import { assertSocialWriteAllowed } from '../../utils/verificationGate';
import {
  clearReviewDraft,
  loadReviewDraft,
  type PartnerReview,
  REVIEW_MAX_LENGTH,
  reviewErrorKey,
  saveReviewDraft,
  savePartnerReview,
} from './reviewsApi';
import { reviewPalette, StarPicker } from './StarRating';

// Write / edit sheet: required 1–5 stars, optional text (max 1,000). The
// unfinished review is kept on the device per partner, so it survives an
// interrupted sign-in, email verification, a lost connection or closing the
// sheet; it's cleared once the review is posted (or deleted).
export default function WriteReviewSheet({ visible, venueId, venueName, existing, onClose, onSaved }: {
  visible: boolean;
  venueId: string;
  venueName: string;
  existing: PartnerReview | null;
  onClose: () => void;
  onSaved: (created: boolean) => void;
}) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const androidKeyboardInsets = useKeyboardInsets();
  const palette = reviewPalette(theme);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const [ratingMissing, setRatingMissing] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [ready, setReady] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  const revealReviewInput = () => {
    // Wait for the keyboard inset/layout animation before scrolling. This is
    // especially important on Android edge-to-edge, where the window itself
    // does not resize when the IME opens.
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
  };

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    setReady(false);
    setErrorKey('');
    setRatingMissing(false);
    setSubmitting(false);
    loadReviewDraft(venueId).then(draft => {
      if (!active) return;
      const base = { rating: existing?.rating ?? 0, text: existing?.text ?? '' };
      const differs = Boolean(draft) && (draft!.rating !== base.rating || draft!.text !== base.text);
      setRating(differs ? draft!.rating || base.rating : base.rating);
      setText(differs ? draft!.text : base.text);
      setDraftRestored(differs);
      setReady(true);
    });
    return () => { active = false; };
  }, [existing, venueId, visible]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const update = (next: { rating?: number; text?: string }) => {
    const draft = { rating: next.rating ?? rating, text: next.text ?? text };
    if (next.rating !== undefined) { setRating(next.rating); setRatingMissing(false); }
    if (next.text !== undefined) setText(next.text);
    if (errorKey) setErrorKey('');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const unchanged = existing && draft.rating === existing.rating && draft.text === existing.text;
      if (unchanged) clearReviewDraft(venueId);
      else saveReviewDraft(venueId, draft);
    }, 300);
  };

  const submit = async () => {
    if (submitting) return;
    if (!rating) {
      setRatingMissing(true);
      return;
    }
    try {
      assertSocialWriteAllowed();
    } catch {
      await saveReviewDraft(venueId, { rating, text });
      setErrorKey('partnerReviews.errors.verifyEmail');
      return;
    }
    setSubmitting(true);
    setErrorKey('');
    try {
      const { created } = await savePartnerReview(venueId, rating, text);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await clearReviewDraft(venueId);
      onSaved(created);
    } catch (error) {
      console.warn('[Tonyte][PartnerReviews] save failed', { venueId, error });
      await saveReviewDraft(venueId, { rating, text });
      setErrorKey(reviewErrorKey(error));
    } finally {
      setSubmitting(false);
    }
  };

  const ratingWord = rating ? t(`partnerReviews.ratingWords.${rating}`) : t('partnerReviews.tapToRate');
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose} statusBarTranslucent>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={[
          styles.backdrop,
          Platform.OS === 'android' && { paddingBottom: androidKeyboardInsets.height },
        ]}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel={t('common.cancel')} />
        <View style={[styles.sheet, { backgroundColor: palette.sheet, borderColor: palette.border }]} accessibilityViewIsModal>
          <LinearGradient
            colors={['rgba(45,212,215,0.12)', 'rgba(139,92,246,0.06)', 'rgba(242,125,38,0.12)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
          <LinearGradient
            colors={['#2DD4D7', '#8B5CF6', '#F27D26']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.reflectiveEdge}
            pointerEvents="none"
          />
          <ScrollView
            ref={scrollRef}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            contentContainerStyle={styles.content}
            bounces={false}
          >
            <View style={[styles.grabber, { backgroundColor: palette.border }]} />
            <View style={styles.iconWrap}>
              <Icon name="edit-3" size={21} color={Colors.sunset} />
            </View>
            <Text style={[styles.title, { color: palette.text }]} accessibilityRole="header">
              {existing ? t('partnerReviews.titleEdit') : t('partnerReviews.titleNew').replace('{name}', venueName)}
            </Text>
            <Text style={[styles.hint, { color: palette.muted }]}>{t('partnerReviews.aboutPartner').replace('{name}', venueName)}</Text>
            {draftRestored ? <Text style={[styles.draft, { color: palette.accent }]}>{t('partnerReviews.draftRestored')}</Text> : null}
            {ready ? (
              <>
                <StarPicker value={rating} onChange={value => update({ rating: value })} label={t('partnerReviews.ratingLabel')} />
                <Text style={[styles.ratingWord, { color: rating ? palette.text : palette.faint }]}>{ratingWord}</Text>
                {ratingMissing ? <Text style={styles.error} accessibilityLiveRegion="polite">{t('partnerReviews.ratingRequired')}</Text> : null}
                <Text style={[styles.label, { color: palette.muted }]}>{t('partnerReviews.textLabel')}</Text>
                <View style={[styles.inputShell, { borderColor: palette.border }]}>
                  <TextInput
                    value={text}
                    onChangeText={value => update({ text: value })}
                    placeholder={t('partnerReviews.placeholder')}
                    placeholderTextColor={palette.faint}
                    multiline
                    maxLength={REVIEW_MAX_LENGTH}
                    textAlignVertical="top"
                    editable={!submitting}
                    onFocus={revealReviewInput}
                    style={[styles.input, { color: palette.text, backgroundColor: palette.input }]}
                    accessibilityLabel={t('partnerReviews.textLabel')}
                    testID="review-text"
                  />
                </View>
                <Text style={[styles.counter, { color: text.length >= REVIEW_MAX_LENGTH ? '#F87171' : palette.faint }]}>
                  {`${text.length}/${REVIEW_MAX_LENGTH}`}
                </Text>
              </>
            ) : <ActivityIndicator color={Colors.sunset} style={styles.loading} />}
            {errorKey ? <Text style={styles.error} accessibilityLiveRegion="polite" testID="review-error">{t(errorKey)}</Text> : null}
            <View style={styles.buttons}>
              <TouchableOpacity style={[styles.button, { backgroundColor: palette.surfaceStrong }]} onPress={onClose} accessibilityRole="button" disabled={submitting}>
                <Text style={[styles.secondaryText, { color: palette.text }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.primary, (!rating || submitting) && styles.dimmed]}
                onPress={submit}
                accessibilityRole="button"
                accessibilityState={{ busy: submitting, disabled: submitting }}
                testID="review-submit"
              >
                <LinearGradient
                  colors={['#FF9A3C', '#F26A18']}
                  start={{ x: 0, y: 0.5 }}
                  end={{ x: 1, y: 0.5 }}
                  style={StyleSheet.absoluteFill}
                />
                {submitting
                  ? <ActivityIndicator color="#fff" />
                  : <Text style={styles.primaryText}>{existing ? t('partnerReviews.update') : t('partnerReviews.submit')}</Text>}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(1,4,13,0.82)' },
  sheet: { borderTopLeftRadius: 30, borderTopRightRadius: 30, borderWidth: 1, borderBottomWidth: 0, maxHeight: '92%', overflow: 'hidden' },
  reflectiveEdge: { position: 'absolute', top: 0, left: 30, right: 30, height: 2, borderRadius: 2 },
  content: { padding: 22, paddingBottom: 30, gap: 11 },
  grabber: { alignSelf: 'center', width: 40, height: 4, borderRadius: 2, marginBottom: 6 },
  iconWrap: { width: 54, height: 54, borderRadius: 18, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(242,125,38,0.36)', backgroundColor: 'rgba(242,125,38,0.10)' },
  title: { fontSize: Typography.lg, fontWeight: Typography.extrabold, textAlign: 'center' },
  hint: { fontSize: 13, lineHeight: 18, textAlign: 'center' },
  draft: { fontSize: 12, fontWeight: Typography.bold, textAlign: 'center' },
  ratingWord: { fontSize: 14, fontWeight: Typography.bold, textAlign: 'center' },
  label: { fontSize: 12, fontWeight: Typography.bold, marginTop: 6 },
  inputShell: { borderWidth: 1, borderRadius: 18, overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.035)' },
  input: { minHeight: 130, maxHeight: 240, padding: 14, fontSize: 15, lineHeight: 21 },
  counter: { fontSize: 11, textAlign: 'right' },
  error: { color: '#F87171', fontSize: 13, lineHeight: 18, textAlign: 'center', fontWeight: Typography.semibold },
  loading: { marginVertical: 40 },
  buttons: { flexDirection: 'row', gap: 10, marginTop: 6 },
  button: { flex: 1, minHeight: 52, borderRadius: 16, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' },
  primary: { backgroundColor: Colors.sunset, borderColor: 'rgba(255,185,56,0.50)' },
  dimmed: { opacity: 0.6 },
  primaryText: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  secondaryText: { fontSize: 15, fontWeight: Typography.semibold },
});
